import type { EntityOutboxEntityType } from '../offline/db'
import { getOfflineDb } from '../offline/dbFactory'
import { ensureOfflineReady } from '../offline/initOffline'
import { drainEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'

export type EntityMutationSyncState = 'offline' | 'pending' | 'synced'

export type EntityBatchSyncBlocker = {
  scope: 'current' | 'queue'
  entityType: EntityOutboxEntityType
  entityId: string
  seq: number | null
  errorMessage: string
}

export type EntityBatchSyncStatus = {
  syncState: EntityMutationSyncState
  blockedCount: number
  errorMessage: string | null
  blockers: EntityBatchSyncBlocker[]
}

export type EntityBatchSyncRetryReport = {
  status: EntityBatchSyncStatus
  skippedNotFoundCount: number
}

export type EntityMutationResult<T> = {
  data: T
  syncState: EntityMutationSyncState
  syncPromise: Promise<EntityMutationSyncState> | null
}

const isOnline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine

const normalizeEntityIds = (entityIds: string[]): string[] => (
  Array.from(new Set(entityIds.map((value) => String(value ?? '').trim()).filter(Boolean)))
)

const summarizeStructuredOutboxError = (value: string): string | null => {
  const buildItemMessage = (item: unknown): string | null => {
    if (!item || typeof item !== 'object') return null

    const candidate = item as {
      message?: unknown
      path?: unknown
    }

    const message = typeof candidate.message === 'string' ? candidate.message.trim() : ''
    const path = Array.isArray(candidate.path)
      ? candidate.path.map((segment) => String(segment ?? '').trim()).filter(Boolean).join('.')
      : ''

    if (!message) return path || null
    if (!path) return message
    return `${path}: ${message}`
  }

  try {
    const parsed = JSON.parse(value) as unknown
    if (Array.isArray(parsed)) {
      const messages = Array.from(new Set(parsed.map(buildItemMessage).filter((item): item is string => Boolean(item))))
      return messages.length > 0 ? messages.join('; ') : null
    }

    return buildItemMessage(parsed)
  } catch {
    return null
  }
}

const normalizeOutboxErrorMessage = (value: string | null | undefined): string | null => {
  const raw = String(value ?? '').trim()
  if (!raw) return null

  const compact = String(summarizeStructuredOutboxError(raw) ?? raw).replace(/\s+/g, ' ').trim()
  if (!compact) return null
  if (compact.length <= 160) return compact
  return `${compact.slice(0, 157).trimEnd()}...`
}

const summarizeOutboxErrorMessages = (messages: string[]): string | null => {
  const uniqueMessages = Array.from(new Set(messages.map(normalizeOutboxErrorMessage).filter((value): value is string => Boolean(value))))
  if (uniqueMessages.length === 0) return null
  if (uniqueMessages.length === 1) return uniqueMessages[0]

  const extraIssues = uniqueMessages.length - 1
  return `${uniqueMessages[0]} (${extraIssues} more issue${extraIssues === 1 ? '' : 's'})`
}

const buildEntityBlockers = (args: {
  scope: 'current' | 'queue'
  entries: Array<{
    entityType: EntityOutboxEntityType
    entityId: string
    seq?: number
    lastError?: string
  }>
}): EntityBatchSyncBlocker[] => {
  const blockersByKey = new Map<string, EntityBatchSyncBlocker>()

  args.entries
    .slice()
    .sort((left, right) => (left.seq ?? Number.MAX_SAFE_INTEGER) - (right.seq ?? Number.MAX_SAFE_INTEGER))
    .forEach((entry) => {
      const nextMessage = normalizeOutboxErrorMessage(entry.lastError)
      const blockerKey = `${entry.entityType}:${entry.entityId}`
      if (!nextMessage || blockersByKey.has(blockerKey)) return

      blockersByKey.set(blockerKey, {
        scope: args.scope,
        entityType: entry.entityType,
        entityId: entry.entityId,
        seq: typeof entry.seq === 'number' ? entry.seq : null,
        errorMessage: nextMessage
      })
    })

  return Array.from(blockersByKey.values())
}

const buildCurrentEntityBlockers = (entries: Array<{
  entityType: EntityOutboxEntityType
  entityId: string
  seq?: number
  lastError?: string
}>): EntityBatchSyncBlocker[] => {
  return buildEntityBlockers({
    scope: 'current',
    entries
  })
}

export const readEntityBatchSyncStatus = async (
  entityType: EntityOutboxEntityType,
  entityIds: string[]
): Promise<EntityBatchSyncStatus> => {
  const stableEntityIds = normalizeEntityIds(entityIds)
  if (stableEntityIds.length === 0) {
    return {
      syncState: 'synced',
      blockedCount: 0,
      errorMessage: null,
      blockers: []
    }
  }

  await ensureOfflineReady()

  const pairs = stableEntityIds.map((entityId) => [entityType, entityId] as [EntityOutboxEntityType, string])
  const entries = await getOfflineDb().entityOutbox
    .where('[entityType+entityId]')
    .anyOf(pairs)
    .toArray()

  if (entries.length === 0) {
    return {
      syncState: 'synced',
      blockedCount: 0,
      errorMessage: null,
      blockers: []
    }
  }

  const currentBlockers = buildCurrentEntityBlockers(entries)

  if (currentBlockers.length === 0) {
    const pendingEntityIds = Array.from(new Set(entries.map((entry) => entry.entityId)))
    const pendingSeqs = entries
      .map((entry) => entry.seq)
      .filter((value): value is number => typeof value === 'number')
    const earliestPendingSeq = pendingSeqs.length > 0 ? Math.min(...pendingSeqs) : null

    if (earliestPendingSeq !== null) {
      const earlierEntries = await getOfflineDb().entityOutbox
        .where('seq')
        .below(earliestPendingSeq)
        .toArray()
      const queueBlockers = buildEntityBlockers({
        scope: 'queue',
        entries: earlierEntries
      })

      if (queueBlockers.length > 0) {
        return {
          syncState: isOnline() ? 'pending' : 'offline',
          blockedCount: pendingEntityIds.length,
          errorMessage: summarizeOutboxErrorMessages(queueBlockers.map((blocker) => blocker.errorMessage)),
          blockers: queueBlockers
        }
      }
    }
  }

  return {
    syncState: isOnline() ? 'pending' : 'offline',
    blockedCount: currentBlockers.length,
    errorMessage: summarizeOutboxErrorMessages(currentBlockers.map((blocker) => blocker.errorMessage)),
    blockers: currentBlockers
  }
}

export const retryEntityBatchSyncStatus = async (params: {
  entityType: EntityOutboxEntityType
  entityIds: string[]
  maxEntries?: number
  maxPasses?: number
}): Promise<EntityBatchSyncStatus> => {
  const report = await retryEntityBatchSyncStatusWithReport(params)
  return report.status
}

export const retryEntityBatchSyncStatusWithReport = async (params: {
  entityType: EntityOutboxEntityType
  entityIds: string[]
  maxEntries?: number
  maxPasses?: number
}): Promise<EntityBatchSyncRetryReport> => {
  const stableEntityIds = normalizeEntityIds(params.entityIds)
  if (stableEntityIds.length === 0) {
    return {
      status: {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      },
      skippedNotFoundCount: 0
    }
  }

  let skippedNotFoundCount = 0

  if (isOnline()) {
    try {
      const drainResult = await drainEntityOutboxNow({
        maxEntries: params.maxEntries ?? 25,
        maxPasses: params.maxPasses ?? 5
      })
      skippedNotFoundCount = drainResult.skippedNotFound
    } catch (error) {
      console.warn('[entityMutationSync] failed to drain entity outbox', error)
    }
  }

  const status = await readEntityBatchSyncStatus(params.entityType, stableEntityIds)
  return {
    status,
    skippedNotFoundCount
  }
}

export const clearEntityBatchSyncQueueBlockers = async (params: {
  entityType: EntityOutboxEntityType
  entityIds: string[]
  maxEntries?: number
  maxPasses?: number
}): Promise<EntityBatchSyncStatus> => {
  const stableEntityIds = normalizeEntityIds(params.entityIds)
  if (stableEntityIds.length === 0) {
    return {
      syncState: 'synced',
      blockedCount: 0,
      errorMessage: null,
      blockers: []
    }
  }

  await ensureOfflineReady()

  const currentStatus = await readEntityBatchSyncStatus(params.entityType, stableEntityIds)
  const queueBlockerSeqs = Array.from(new Set(
    currentStatus.blockers
      .filter((blocker) => blocker.scope === 'queue' && typeof blocker.seq === 'number')
      .map((blocker) => blocker.seq as number)
  ))

  if (queueBlockerSeqs.length === 0) {
    return currentStatus
  }

  await getOfflineDb().entityOutbox.bulkDelete(queueBlockerSeqs)

  return retryEntityBatchSyncStatus(params)
}

const readEntitySyncState = async (
  entityType: EntityOutboxEntityType,
  entityId: string
): Promise<EntityMutationSyncState> => {
  const status = await readEntityBatchSyncStatus(entityType, [entityId])
  return status.syncState
}

export const syncedEntityMutationResult = <T,>(data: T): EntityMutationResult<T> => ({
  data,
  syncState: 'synced',
  syncPromise: null
})

export const buildQueuedEntityMutationResult = <T,>(
  data: T,
  params: {
    entityType: EntityOutboxEntityType
    entityId: string
    maxEntries?: number
    maxPasses?: number
  }
): EntityMutationResult<T> => {
  if (!isOnline()) {
    return {
      data,
      syncState: 'offline',
      syncPromise: null
    }
  }

  const syncPromise = (async (): Promise<EntityMutationSyncState> => {
    try {
      await drainEntityOutboxNow({
        maxEntries: params.maxEntries ?? 25,
        maxPasses: params.maxPasses ?? 5
      })
    } catch (error) {
      console.warn('[entityMutationSync] failed to drain entity outbox', error)
    }

    return readEntitySyncState(params.entityType, params.entityId)
  })()

  return {
    data,
    syncState: 'pending',
    syncPromise
  }
}